// SPDX-License-Identifier: AGPL-3.0-or-later

import type {CompanionRecording} from '@app/features/companion/commands/CompanionCommands';
import * as CompanionCommands from '@app/features/companion/commands/CompanionCommands';
import styles from '@app/features/companion/components/records/CompanionRecords.module.css';
import {PAUSE_DESCRIPTOR, PLAY_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {formatDuration} from '@fluxer/date_utils/src/DateDuration';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {PauseIcon, PlayIcon} from '@phosphor-icons/react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const LOAD_RECORDINGS_FAILED_DESCRIPTOR = msg({
	message: 'Failed to load recordings',
	comment: 'Error shown when companion recordings cannot be loaded for playback from the history list.',
});
const NO_RECORDINGS_DESCRIPTOR = msg({
	message: 'No recordings available',
	comment: 'Error shown when a companion history session has no recordings to play.',
});
const AUDIO_CHIP_BAR_COUNT = 28;
let stopActiveAudioChip: (() => void) | null = null;

interface CompanionSessionAudioChipProps {
	readonly sessionId: string;
	readonly durationSeconds: number;
	readonly className?: string;
}

function buildWaveformBarHeights(seed: string, count: number): Array<number> {
	const heights: Array<number> = [];
	let state = 2166136261;
	for (let index = 0; index < seed.length; index += 1) {
		state = (state ^ seed.charCodeAt(index)) >>> 0;
		state = Math.imul(state, 16777619) >>> 0;
	}
	for (let index = 0; index < count; index += 1) {
		state = (Math.imul(state, 1103515245) + 12345) >>> 0;
		heights.push(25 + (state % 66));
	}
	return heights;
}

function sumDurationSecondsBefore(parts: ReadonlyArray<CompanionRecording>, index: number): number {
	let total = 0;
	for (let part = 0; part < index; part += 1) total += parts[part].duration_ms / 1000;
	return total;
}

export function CompanionSessionAudioChip({sessionId, durationSeconds, className}: CompanionSessionAudioChipProps) {
	const {i18n} = useLingui();
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const objectUrlsRef = useRef(new Map<string, string>());
	const requestTokenRef = useRef(0);
	const stopRegistrationRef = useRef<(() => void) | null>(null);
	const [parts, setParts] = useState<Array<CompanionRecording> | null>(null);
	const [partIndex, setPartIndex] = useState(0);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [elapsedSeconds, setElapsedSeconds] = useState(0);
	const totalSeconds =
		parts && parts.length > 0 ? parts.reduce((total, part) => total + part.duration_ms / 1000, 0) : durationSeconds;
	const isFinished = totalSeconds > 0 && elapsedSeconds >= totalSeconds;
	const barHeights = useMemo(() => buildWaveformBarHeights(sessionId, AUDIO_CHIP_BAR_COUNT), [sessionId]);
	const playedBarCount = Math.floor(
		(totalSeconds > 0 ? Math.min(1, elapsedSeconds / totalSeconds) : 0) * AUDIO_CHIP_BAR_COUNT,
	);

	const reportError = useCallback(
		(loadError: unknown) => {
			setError(
				CompanionCommands.getCompanionCommandErrorMessage(loadError) ?? i18n._(LOAD_RECORDINGS_FAILED_DESCRIPTOR),
			);
			setIsLoading(false);
			setIsPlaying(false);
		},
		[i18n],
	);

	const resolvePartUrl = useCallback(
		(sortedParts: ReadonlyArray<CompanionRecording>, index: number): Promise<string> => {
			const recording = sortedParts[index];
			if (!recording) return Promise.reject(new Error('Recording part unavailable'));
			const cachedUrl = objectUrlsRef.current.get(recording.id);
			if (cachedUrl) return Promise.resolve(cachedUrl);
			return CompanionCommands.getRecordingAudio(sessionId, recording.id).then((blob) => {
				const url = URL.createObjectURL(blob);
				objectUrlsRef.current.set(recording.id, url);
				return url;
			});
		},
		[sessionId],
	);

	const startPlayback = useCallback(
		(sortedParts: ReadonlyArray<CompanionRecording>, index: number, resetTime: boolean) => {
			const audio = audioRef.current;
			if (!audio) return;
			const requestToken = requestTokenRef.current;
			setIsLoading(true);
			setError(null);
			resolvePartUrl(sortedParts, index)
				.then((url) => {
					if (requestToken !== requestTokenRef.current) return;
					setPartIndex(index);
					if (resetTime || audio.src !== url) {
						audio.src = url;
						audio.currentTime = 0;
						setElapsedSeconds(sumDurationSecondsBefore(sortedParts, index));
					}
					return audio.play().then(() => {
						if (requestToken !== requestTokenRef.current) return;
						stopActiveAudioChip?.();
						stopActiveAudioChip = () => {
							audio.pause();
						};
						stopRegistrationRef.current = stopActiveAudioChip;
						setIsPlaying(true);
						setIsLoading(false);
					});
				})
				.catch((loadError: unknown) => {
					if (requestToken !== requestTokenRef.current) return;
					reportError(loadError);
				});
		},
		[reportError, resolvePartUrl],
	);

	const togglePlayback = useCallback(() => {
		const audio = audioRef.current;
		if (!audio || isLoading) return;
		if (isPlaying) {
			audio.pause();
			setIsPlaying(false);
			return;
		}
		requestTokenRef.current += 1;
		if (!parts) {
			const requestToken = requestTokenRef.current;
			setIsLoading(true);
			setError(null);
			CompanionCommands.listRecordings(sessionId).then(
				(response) => {
					if (requestToken !== requestTokenRef.current) return;
					const sortedParts = [...response.recordings].sort((a, b) => a.part - b.part);
					if (sortedParts.length === 0) {
						setIsLoading(false);
						setError(i18n._(NO_RECORDINGS_DESCRIPTOR));
						return;
					}
					setParts(sortedParts);
					startPlayback(sortedParts, 0, true);
				},
				(loadError: unknown) => {
					if (requestToken !== requestTokenRef.current) return;
					reportError(loadError);
				},
			);
			return;
		}
		startPlayback(parts, isFinished ? 0 : partIndex, isFinished);
	}, [i18n, isFinished, isLoading, isPlaying, partIndex, parts, reportError, sessionId, startPlayback]);

	const handleEnded = useCallback(() => {
		const nextIndex = partIndex + 1;
		if (parts && nextIndex < parts.length) {
			startPlayback(parts, nextIndex, true);
		} else {
			setIsPlaying(false);
			setElapsedSeconds(totalSeconds);
		}
	}, [partIndex, parts, startPlayback, totalSeconds]);
	const handleTimeUpdate = useCallback(() => {
		const audio = audioRef.current;
		if (!audio || !parts) return;
		setElapsedSeconds(sumDurationSecondsBefore(parts, partIndex) + audio.currentTime);
	}, [partIndex, parts]);

	useEffect(() => {
		requestTokenRef.current += 1;
		return () => {
			requestTokenRef.current += 1;
			const audio = audioRef.current;
			if (audio) {
				audio.pause();
				audio.removeAttribute('src');
			}
			for (const url of objectUrlsRef.current.values()) URL.revokeObjectURL(url);
			objectUrlsRef.current.clear();
			if (stopActiveAudioChip === stopRegistrationRef.current) stopActiveAudioChip = null;
		};
	}, [sessionId]);

	return (
		<div
			className={className ? `${styles.audioChip} ${className}` : styles.audioChip}
			data-flx="companion.records.companion-session-audio-chip.container"
		>
			<button
				type="button"
				className={styles.audioChipButton}
				onClick={togglePlayback}
				aria-label={i18n._(isPlaying ? PAUSE_DESCRIPTOR : PLAY_DESCRIPTOR)}
				aria-busy={isLoading}
				data-flx="companion.records.companion-session-audio-chip.button.toggle-playback"
			>
				{isPlaying ? (
					<PauseIcon weight="fill" aria-hidden data-flx="companion.records.companion-session-audio-chip.pause-icon" />
				) : (
					<PlayIcon weight="fill" aria-hidden data-flx="companion.records.companion-session-audio-chip.play-icon" />
				)}
			</button>
			<span
				className={styles.audioChipWaveform}
				aria-hidden
				data-flx="companion.records.companion-session-audio-chip.waveform"
			>
				{barHeights.map((height, index) => (
					<span
						key={index}
						className={index < playedBarCount ? styles.audioChipBarPlayed : styles.audioChipBar}
						style={{height: `${height}%`}}
						data-flx="companion.records.companion-session-audio-chip.waveform-bar"
					/>
				))}
			</span>
			<span className={styles.audioChipDuration} data-flx="companion.records.companion-session-audio-chip.duration">
				{formatDuration(totalSeconds, i18n.locale)}
			</span>
			{error ? (
				<span
					className={styles.audioChipError}
					role="alert"
					data-flx="companion.records.companion-session-audio-chip.error"
				>
					{error}
				</span>
			) : null}
			<audio
				ref={audioRef}
				onEnded={handleEnded}
				onTimeUpdate={handleTimeUpdate}
				onPause={() => setIsPlaying(false)}
				data-flx="companion.records.companion-session-audio-chip.audio"
			>
				<track kind="captions" data-flx="companion.records.companion-session-audio-chip.track" />
			</audio>
		</div>
	);
}
